/**
 * ⚠ CLINICAL CONTENT — sourced from the FFP Prenatal Breastfeeding Handbook
 * outline (draft v1) and standard newborn guidance. Every string in this
 * file needs sign-off from the FFP lactation team (Nicki) before release.
 */
export const content = {
  congrats: {
    title: 'Congratulations!',
    titleWithName: (name: string) => `Congratulations! Welcome, ${name}!`,
    body: 'You did an amazing thing. These first days are all about rest, feeding, and getting to know each other. We are here for you the whole way.',
    partnerBody: 'A new baby is here! Your support in these first weeks makes a real difference for both mom and baby.',
  },

  checkup: {
    title: 'Schedule the newborn checkup',
    body: 'Your pediatrician should see the baby at 3 to 5 days old to check weight, feeding, and jaundice. Booking it now means the visit lands in the right window.',
    partnerBody: 'One of the most helpful things you can do right now: make sure the 3-5 day newborn checkup is on the calendar.',
    scheduleOnline: 'Schedule online',
    callToSchedule: 'Call to schedule',
    markScheduled: 'I booked it',
    scheduledFor: (date: string) => `Booked for ${date}`,
    whenIsIt: 'When is the visit?',
  },

  visits: {
    newborn: {
      name: 'Newborn checkup',
      window: 'Age 3 to 5 days',
      desc: 'Weight, feeding, and jaundice check. This is the big one to book right away.',
    },
    lactation: {
      name: 'Lactation follow-up',
      window: 'Day 4 to 7, once milk is in',
      desc: 'An IBCLC watches a full feeding, checks latch and weight transfer, and sends you home with a plan.',
    },
    twoWeek: {
      name: 'Two-week check',
      window: 'Around 2 weeks',
      desc: 'Weight check. Most babies are back to birth weight by now.',
    },
    oneMonth: {
      name: 'One-month well visit',
      window: 'Around 1 month',
      desc: 'Standard well-child visit.',
    },
  },

  stages: {
    colostrum: {
      name: 'Colostrum days',
      desc: 'Tiny volumes are normal. Colostrum comes in teaspoons, not ounces, and it is exactly what your baby needs right now.',
    },
    transitional: {
      name: 'Milk coming in',
      desc: 'Milk typically transitions around day 3 to 5. Cluster feeding is normal and helps bring milk in. A cesarean, long labor, significant blood loss, or separation from baby can add a day or two.',
    },
    milkIn: {
      name: 'Milk is in',
      desc: 'Expect roughly 1 to 3 oz per feeding and 8 to 12 feedings each 24 hours. Wet diapers should reach 6 or more a day.',
    },
  },

  expectations: {
    title: 'What to expect today',
    feeds: (min: number, max: number) => `${min} to ${max} feedings per 24 hours`,
    wets: (min: number) => `At least ${min} wet ${min === 1 ? 'diaper' : 'diapers'}`,
    dirties: (min: number) => `At least ${min} ${min === 1 ? 'stool' : 'stools'}`,
    intake: (range: string) => `About ${range} per feeding`,
    wakeToFeed:
      'A sleepy, easy baby still needs to eat. In the first two weeks, wake your baby to feed if it has been 3 hours during the day or 4 hours at night.',
    weightNote:
      'Weight loss up to 7% of birth weight is within the normal range. Babies are usually back to birth weight by 2 weeks.',
  },

  whenToCall: {
    title: 'When to reach out',
    lactationLine: {
      title: 'Call or text the lactation team',
      items: [
        'Painful latch, or nipples that are cracked or bleeding',
        'Engorgement that is not improving',
        'Worries about milk supply, too little or too much',
        'Pumping or flange-fit questions',
      ],
    },
    office: {
      title: 'Call the office',
      items: [
        'Fewer wet or dirty diapers than expected for the day',
        'Fewer than 8 feedings in 24 hours, or a baby too sleepy to wake for feeds',
        'Skin or eyes looking more yellow',
        'Fever, redness, or a painful hard area in the breast (possible mastitis)',
        'Feeling persistently down, anxious, or overwhelmed',
      ],
    },
    urgent: {
      title: 'Seek care right away',
      items: [
        'Rectal temperature of 100.4°F (38°C) or higher in a baby under 2 months',
        'No wet diaper in 24 hours, or signs of dehydration',
        'Baby is limp, very hard to wake, or breathing with difficulty',
        'Blue or gray lips or face',
      ],
    },
  },

  mood: {
    title: 'Your perinatal mood',
    normal:
      'Weepy, tired, and overwhelmed moments in the first two weeks ("baby blues") are common and usually pass.',
    concerning:
      'If sadness or anxiety is intense, lasts beyond two weeks, or keeps you from sleeping when you could, tell us. Postpartum depression and anxiety are common and treatable, and we can help.',
    crisis:
      'If you have thoughts of harming yourself or your baby, call or text 988 (Suicide & Crisis Lifeline) now. You can also reach Postpartum Support International at 1-800-944-4773.',
    partnerNote:
      'Partners: you know her baseline. If mom does not seem like herself for more than two weeks, gently help her reach out. Partners can experience postpartum depression too.',
  },

  partnerTips: {
    title: 'Ways to support mom today',
    intro:
      'Recovery and milk production take everything mom has. These small jobs make a big difference.',
    tips: [
      {
        title: 'Bring baby for feeds',
        body: 'Especially at night and after a cesarean. Change the diaper, bring baby to mom, and handle burping after.',
      },
      {
        title: 'Protect sleep and feeding time',
        body: 'Guard her naps. Keep feeding sessions calm, unhurried, and interruption-free.',
      },
      {
        title: 'Track the diapers',
        body: 'Own the diaper log in this app. Wet and dirty counts are the best sign baby is getting enough.',
      },
      {
        title: 'Water and snacks, always',
        body: 'Breastfeeding is thirsty work. Refill her water every time she sits down to feed, and keep one-handed snacks nearby.',
      },
      {
        title: 'Handle the visitors',
        body: 'Be the gatekeeper. Short visits, no drop-ins, and it is always okay to say "not today."',
      },
      {
        title: 'Get your skin-to-skin time',
        body: 'Skin-to-skin calms baby, steadies temperature and breathing, and is your bonding time too.',
      },
      {
        title: 'Book the appointments',
        body: 'Make sure the newborn checkup (3 to 5 days) and lactation follow-up are scheduled, and plan to be there.',
      },
      {
        title: 'Watch her mood',
        body: 'Baby blues are common in the first two weeks. If mom does not seem like herself beyond that, help her call the office.',
      },
    ],
  },

  prenatal: {
    title: 'Before baby arrives',
    countdown: (days: number) => (days === 1 ? '1 day until your due date' : `${days} days until your due date`),
    pastDue: 'Any day now!',
    intro:
      'This app comes to life when your baby arrives. Until then, here is how to get ready.',
    checklist: [
      'Attend your prenatal breastfeeding consultation',
      'Order your breast pump through insurance',
      'Ask about collecting colostrum after 37 weeks (check with your OB or midwife first)',
      'Plan for skin-to-skin and a first feed within the first hour',
      'Pick your feeding goals; combination feeding is valid too',
      'Add the lactation line to your phone contacts',
    ],
    colostrumNote:
      'Hand expression of colostrum is generally only advised after 37 weeks and after checking with your OB or midwife, since it can trigger contractions and is not recommended in some pregnancies.',
    babyIsHere: 'Baby is here!',
  },
};
